import { create } from 'zustand'

// Helper to get initial AI panel state from localStorage
const getInitialAIPanelState = (): boolean => {
  try {
    const saved = localStorage.getItem('apex_ai_panel_open')
    return saved !== null ? saved === 'true' : false
  } catch {
    return false
  }
}

interface UIState {
  sidebarCollapsed: boolean;
  sidebarMobileOpen: boolean;
  activeTicker: string | null;
  tickerPanelOpen: boolean;
  activeModal: string | null;
  aiPanelOpen: boolean;
  aiPanelPrompt: string | null;
  aiPanelManuallyOpened: boolean;
  commandPaletteOpen: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleMobileSidebar: () => void;
  setMobileSidebarOpen: (open: boolean) => void;
  openTickerPanel: (ticker: string) => void;
  closeTickerPanel: () => void;
  openModal: (modalId: string) => void;
  closeModal: () => void;
  toggleAIPanel: () => void;
  setAIPanelOpen: (open: boolean, manual?: boolean) => void;
  sendAIPrompt: (prompt: string) => void;
  clearAIPrompt: () => void;
  openCommandPalette: () => void;
  closeCommandPalette: () => void;
}

export const useUIStore = create<UIState>((set) => ({
  sidebarCollapsed: false,
  sidebarMobileOpen: false,
  activeTicker: null,
  tickerPanelOpen: false,
  activeModal: null,
  aiPanelOpen: getInitialAIPanelState(),
  aiPanelPrompt: null,
  aiPanelManuallyOpened: false,
  commandPaletteOpen: false,

  toggleSidebar: () => set((state) => ({
    sidebarCollapsed: !state.sidebarCollapsed
  })),

  setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),

  toggleMobileSidebar: () => set((state) => ({
    sidebarMobileOpen: !state.sidebarMobileOpen
  })),

  setMobileSidebarOpen: (sidebarMobileOpen) => set({ sidebarMobileOpen }),

  openTickerPanel: (ticker) => set({
    activeTicker: ticker,
    tickerPanelOpen: true
  }),

  closeTickerPanel: () => set({
    tickerPanelOpen: false,
    activeTicker: null
  }),

  openModal: (modalId) => set({ activeModal: modalId }),

  closeModal: () => set({ activeModal: null }),

  toggleAIPanel: () => set((state) => {
    const newState = !state.aiPanelOpen
    try {
      localStorage.setItem('apex_ai_panel_open', String(newState))
    } catch {}
    return {
      aiPanelOpen: newState,
      aiPanelManuallyOpened: newState, // Track manual toggle
    }
  }),

  setAIPanelOpen: (aiPanelOpen, manual = false) => set(() => {
    try {
      localStorage.setItem('apex_ai_panel_open', String(aiPanelOpen))
    } catch {}
    return {
      aiPanelOpen,
      aiPanelManuallyOpened: manual && aiPanelOpen,
    }
  }),

  sendAIPrompt: (prompt) => set({
    aiPanelOpen: true,
    aiPanelPrompt: prompt
  }),

  clearAIPrompt: () => set({ aiPanelPrompt: null }),

  openCommandPalette: () => set({ commandPaletteOpen: true }),
  closeCommandPalette: () => set({ commandPaletteOpen: false }),
}))
